import { Github } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Reveal } from '@/components/reveal';
import { useLocale } from '@/i18n/locale-context';

export function OpenSourceSection() {
  const { t } = useLocale();

  return (
    <section className="relative mx-auto w-full max-w-7xl overflow-hidden px-6 py-24 sm:px-10 sm:py-32">
      {/* Background with subtle grid */}
      <div className="absolute inset-0 -z-10 bg-muted/20" />
      <div
        className="absolute inset-0 -z-10 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <Reveal>
        <div className="grid items-center gap-12 md:grid-cols-2">
          {/* Left: text content */}
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t.openSource.eyebrow}
            </p>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.openSource.title}
            </h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {t.openSource.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t.openSource.badge}
              </span>
              <Button variant="outline" size="sm" asChild>
                <a
                  href="https://github.com/fustar-research"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <Github className="h-4 w-4" />
                  {t.openSource.cta}
                </a>
              </Button>
            </div>
          </div>

          {/* Right: code block decoration */}
          <div className="hidden md:block">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-xl border border-primary/20 bg-card/60 backdrop-blur-sm transition-colors duration-300 hover:border-primary/40">
                <div className="flex items-center gap-1.5 border-b border-border/60 px-4 py-2.5">
                  <span className="h-2 w-2 rounded-full bg-red-400/70" />
                  <span className="h-2 w-2 rounded-full bg-amber-300/50" />
                  <span className="h-2 w-2 rounded-full bg-emerald-400/60" />
                  <span className="ml-2 font-mono-code text-[10px] text-muted-foreground">
                    fusion-hub / plugins
                  </span>
                </div>
                <pre className="overflow-x-auto p-4 font-mono-code text-[11px] leading-6 text-muted-foreground">
                  <span className="text-muted-foreground/40">// compute-duckdb plugin</span>
                  {'\n'}
                  <span className="text-cyan-300/90">pub mod</span>{' '}
                  <span className="text-foreground/85">executor</span>;{' '}
                  <span className="text-muted-foreground/40">// hub compute</span>
                  {'\n'}
                  <span className="text-cyan-300/90">pub mod</span>{' '}
                  <span className="text-foreground/85">manifest</span>;{' '}
                  <span className="text-muted-foreground/40">// plugin schema</span>
                  {'\n'}
                  <span className="text-cyan-300/90">pub mod</span>{' '}
                  <span className="text-foreground/85">vision</span>;{' '}
                  <span className="text-muted-foreground/40">// visual blocks</span>
                  {'\n'}
                  {'\n'}
                  <span className="text-cyan-300/90">fn</span>{' '}
                  <span className="text-foreground/85">main</span>() {'{'}{'\n'}
                  {'  '}println!(<span className="text-amber-200/70">&quot;Hello, Fustar!&quot;</span>);{'\n'}
                  {'}'}
                </pre>
              </div>
            </Reveal>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
